<script setup lang="ts">
import TripCardSection from '../card-section/sections/TripCardSection.vue';
import AutoReserveCardSection from '../card-section/sections/AutoReserveCardSection.vue';
import TripCardLoader from '../card-section/sections/TripCardLoader.vue';
import ExtraReserveCard from './ExtraReserveCard.vue';
import { useHandleEvents } from '@composable/_local/hotel&view/useHandleEvents';
import type TripPackage from '@itinerary/trip/TripPackage';
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';

const emit = defineEmits<{
    viewPromotedList: [];
    viewReservableList: [];
    viewNonReservableList: [];
}>();

const itineraryStore = useItineraryStore();

const siteConfigs = useSiteConfigs();

const { login } = useAuth();

const { selectPackage } = usePackageSelectStore();
const handleEvents = useHandleEvents;

const {
    reservableList,
    lowestPricePromoted,
    promotedList,
    isLoadedSuccess,
    nonReservableList,
    hasAutoReserve,
    loading,
} = storeToRefs(itineraryStore);
const currentModule = useCurrentItineraryModule();
const { currentLoadingStatus, isRealTimeLoading } = moduleToRefs(
    currentModule.value
);

const layoutStore = useLayoutStore();
const { isExtraFeaturesEnabled, normalSupportNumber } =
    classToRefs(layoutStore);
const isMounted = useMounted();

const isLoading = computed(
    () =>
        loading.value ||
        currentLoadingStatus.value === undefined ||
        currentLoadingStatus.value === LoadingStatus.None ||
        !process.client ||
        !isMounted.value
);

const route = useRoute();

const reserveItem = async (
    item: TripPackage,
    index: number,
    bypassRaise?: boolean
) => {
    if (!item.reservable) {
        return;
    }
    if (item.callToReserve) {
        location.href = `tel:+9821${normalSupportNumber.value}`;
        return;
    }
    if (item.reservationUrl) {
        window.open(item.reservationUrl, '_blank');
        return;
    }
    if (item.needsLoginForDiscount) {
        try {
            const isAuthenticated = await login({
                message:
                    'برای بهره‌مندی از تخفیف به حساب کاربری خود وارد شوید.',
                rejectionText: 'خرید بدون عضویت',
                openedBy: 'reserveCard',
            });
            if (!isAuthenticated) {
                return;
            }
        } catch (ex) {}
    }
    handleEvents(item, index);
    if (item.isFlightPackage()) {
        item.defaultPrice?.loadBaggage();
    }
    await selectPackage({
        index: itineraryStore.currentSetIndex,
        package: item,
        bypassRaise,
    });
    scrollTo({ top: 0, behavior: 'smooth' });
};

watch(loading, value => {
    if (!value) {
        const { key } = route.query;
        if (key) {
            const reserveItemIndex = reservableList.value.findIndex(
                ip => ip.uniqueId === key
            );
            if (reserveItemIndex !== -1) {
                const reserveItemValue = reservableList.value[reserveItemIndex];
                reserveItem(
                    reserveItemValue as TripPackage,
                    reserveItemIndex,
                    true
                );
            }
        }
    }
});
</script>
<template>
    <TripCardLoader v-if="isLoading" :count="4" />
    <template v-else>
        <TripCardSection
            v-if="lowestPricePromoted && isLoadedSuccess"
            key="promoted-list"
            class="promoted-cart-container"
            :list="[lowestPricePromoted] as TripPackage[]"
            @view-section="emit('viewPromotedList')"
        />
        <TripCardSection
            v-if="promotedList.length && isLoadedSuccess"
            key="promoted-list"
            class="promoted-cart-container"
            :title="`پیشنهاد ویژه ${siteConfigs.title}`"
            :list="promotedList as TripPackage[]"
            @view-section="emit('viewPromotedList')"
        />
        <TripCardSection
            key="reservable-list"
            class="cards-container"
            :list="reservableList as TripPackage[]"
            display-banners
            display-price-track
            :display-taxi-offer="isExtraFeaturesEnabled"
            real-time-loading
            @button-clicked="reserveItem"
            @view-section="emit('viewReservableList')"
        >
            <template #extra-item>
                <ExtraReserveCard />
            </template>
        </TripCardSection>
        <ClientOnly>
            <Transition>
                <AutoReserveCardSection
                    v-if="hasAutoReserve && !isRealTimeLoading"
                />
            </Transition>
        </ClientOnly>
        <TripCardSection
            v-if="
                nonReservableList.length && currentLoadingStatus === 'success'
            "
            key="none-reservable-list"
            :title="
                hasAutoReserve ? 'غیرقابل رزرو' : 'غیرقابل رزرو (تکمیل ظرفیت)'
            "
            :list="nonReservableList as TripPackage[]"
            @view-section="emit('viewNonReservableList')"
        />
    </template>
</template>
<style scoped lang="scss">
.cards-container {
    margin-top: 4px;
}

.promoted-cart-container {
    :deep(.trip-card-wrapper) {
        border: 1px solid usePalette();
    }
}
</style>
